// @givmo/fundraisers-module — the organization contact offer's copy, wire, rule and write: the org
// grain of the §317(a) offer (FCA-BE12, D-FCM-15; rows EP-WEB15 and FCM-WEB6).
//
// Two reads carry the offer, and only while the backend's FUNDRAISER_ORG_CONTACT_ENABLED is on — no
// key, no offer, so its presence is the server's call and no web switch exists:
//   • the owner's PRIVATE fundraiser read (`GET /users/me/fundraisers/{id}` and the `PUT` answer):
//     `share_org_contact` + `org_contact_email` — the employer portal;
//   • the `/org` detail and mutation answers: `contact_offer{shared, name, email, email_domain}` —
//     this module's editor.
// Either way the address is the one the server offers THIS viewer (exec-gc consult #4's edit B):
// their own while the company does not share and the stored address is another person's own,
// otherwise the stored address, and none on a row created before the offer existed. The UI never
// re-derives that rule: it prefills what it is given, and a switch-on that leaves the address alone
// writes the switch only, so the server applies it.

import { firstErrorCode, isGivmoError } from '@givmo/api-client';

/**
 * The approved copy (round-3b frames, as amended by exec-gc consult #4 edit A). Jonathan's D-GRP-76
 * (2026-11-02, "get rid of this explainer text here and anywhere else it appears under 'share my
 * contact info with charity'") took away the line under the switch ("They can use it to thank {org}
 * …") on every client, overriding the set's clearance for it. Nothing here may imply the
 * organization's name can be hidden (D-FCM-13).
 */
export const ORG_CONTACT_OFFER_COPY = {
  heading: 'What charities see',
  notice: (org: string) =>
    `Charities this fundraiser supports, including through a collection or Cause fund, see it in their Givmo charity portal with ${org}’s name as it appears on the fundraiser page.`,
  title: 'Share a contact email with these charities',
  field: 'Contact email',
  /** A company with no usable domain accepts only an admin's own address (the server's own words). */
  helper: (domain: string | null) =>
    domain
      ? `Use your own email or any address ending in @${domain}.`
      : 'Use your own email address.',
  invalid: (suggestion: string | null, domain: string | null) =>
    suggestion && domain
      ? `Use ${suggestion} or an email address ending in @${domain}.`
      : domain
        ? `Use an email address ending in @${domain}.`
        : suggestion
          ? `Use ${suggestion}.`
          : 'Use your own email address.',
} as const;

/** The offer as a read states it. */
export interface OrgContactOfferState {
  /** The organization chose to share. */
  shared: boolean;
  /** The address the server offers this viewer, or null on a row that predates the offer. */
  email: string | null;
}

/** The `/org` read's fuller offer. */
export interface OrgContactOfferDetail extends OrgContactOfferState {
  /** The organization's public name, as a recipient charity sees it. */
  name: string | null;
  /** The domain any other address must be on; null when the organization has none usable. */
  domain: string | null;
}

/** The server's cap (RFC 5321), and the column's width. */
export const ORG_CONTACT_EMAIL_MAX_LENGTH = 320;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function address(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/** The offer on the owner's private read, or null when the read carries none (the switch is off). */
export function contactOfferFromPrivateRead(fundraiser: unknown): OrgContactOfferState | null {
  if (!isRecord(fundraiser) || typeof fundraiser.share_org_contact !== 'boolean') return null;
  return { shared: fundraiser.share_org_contact, email: address(fundraiser.org_contact_email) };
}

/** `OrgFundraiserOut.contact_offer`, or null when absent (the switch off, or a charity's row). */
export function contactOfferFromOrgRead(row: unknown): OrgContactOfferDetail | null {
  const offer = isRecord(row) ? row.contact_offer : undefined;
  if (!isRecord(offer) || typeof offer.shared !== 'boolean') return null;
  return {
    shared: offer.shared,
    email: address(offer.email),
    name: address(offer.name),
    domain: address(offer.email_domain),
  };
}

/** What the inline check needs. The server stays the authority: its 422 is shown the same way. */
export interface OrgContactRule {
  /** The viewer's own account email — accepted while they administer the organization. */
  ownEmail: string | null;
  /** The address the read offered — accepted as it stands. */
  offeredEmail: string | null;
  /** The organization's email domain; null when it has none usable, so only a known address passes. */
  domain: string | null;
}

/**
 * Why this choice cannot be written, or null when it can. Sharing OFF never has a problem: nothing is
 * checked and nothing is held back. Sharing ON needs a known address (the viewer's own, or the one
 * the read offered; case-insensitive), or one that parses as an email and whose host IS the
 * organization's domain, case-insensitive — subdomains and parent domains are refused, the rule the
 * admin roster already applies (proposal rev 3 §2.3). Mirrors `validate_org_contact_email`.
 */
export function orgContactProblem(
  shared: boolean,
  email: string,
  rule: OrgContactRule,
): string | null {
  if (!shared) return null;
  const value = email.trim();
  const fix = ORG_CONTACT_OFFER_COPY.invalid(rule.ownEmail ?? rule.offeredEmail, rule.domain);
  if (value === '') return fix;
  const folded = value.toLowerCase();
  if ([rule.ownEmail, rule.offeredEmail].some((known) => known?.toLowerCase() === folded))
    return null;
  const at = value.indexOf('@');
  const parses =
    value.length <= ORG_CONTACT_EMAIL_MAX_LENGTH &&
    !/\s/.test(value) &&
    at > 0 &&
    at === value.lastIndexOf('@');
  const host = folded.slice(at + 1);
  return parses && rule.domain !== null && host === rule.domain.toLowerCase() ? null : fix;
}

/** The write the offer's fields add to a save. */
export interface OrgContactWrite {
  share_org_contact?: boolean;
  org_contact_email?: string;
}

/**
 * What a save must send for this choice, measured against the read, or null when nothing changed.
 * The choice travels when it changed. The address travels only while sharing is ON and only when it
 * differs from what the read offered — so a switch-on that keeps the offered address writes the
 * switch alone and the server resolves whose address it is (edit B); a row with no address sends the
 * prefilled one. Sharing OFF sends only the choice, whatever the hidden field holds.
 */
export function orgContactWrite(
  shared: boolean,
  email: string,
  held: OrgContactOfferState,
): OrgContactWrite | null {
  const write: OrgContactWrite = {};
  if (shared !== held.shared) write.share_org_contact = shared;
  if (shared && email.trim() !== (held.email ?? '')) {
    write.share_org_contact = true;
    write.org_contact_email = email.trim();
  }
  return Object.keys(write).length > 0 ? write : null;
}

/** The server's refusal of the offer (the `org_contact_invalid` 422), in its own words; else null. */
export function orgContactRefusal(error: unknown): string | null {
  return isGivmoError(error) && firstErrorCode(error) === 'org_contact_invalid'
    ? error.message
    : null;
}
